import React, { useEffect, useRef, useState } from 'react';

/**
 * One labelled stage of a long-running operation.
 * `weight` is the relative share of the estimated duration this stage occupies
 * (any positive number; the values are normalised internally). Use it to make
 * the label dwell on the genuinely slow stage (usually the LLM call).
 */
export interface ProgressStep {
  label: string;
  weight: number;
}

interface TimedProgressProps {
  /** True while the underlying operation is in flight. */
  active: boolean;
  /** Assumed total duration in ms; drives the simulated bar speed. */
  estimatedDurationMs: number;
  /** Ordered stages with relative weights, mapped to the real backend steps. */
  steps: ProgressStep[];
  /** Optional heading shown above the bar. */
  title?: string;
  /** 'card' centres the bar in a padded container; 'inline' renders bar-only. */
  variant?: 'card' | 'inline';
  /** Optional extra className on the root element. */
  className?: string;
}

const TICK_MS = 200;
// Never show a "finished" bar while the request is still in flight: ease toward
// this ceiling and hold there. The bar only reaches 100% once `active` is false.
const MAX_PCT = 95;
// Easing rate. At elapsed == estimate the bar sits ~90%; beyond that it keeps
// creeping toward MAX_PCT so a slower-than-expected run never looks stuck.
const EASE_K = 3;

/**
 * A time-based ("simulated") progress bar for blocking operations that cannot
 * report real progress (the backend is a single blocking request — no streaming).
 * It advances on the assumed duration, shows the current activity label, eases
 * toward ~95% while waiting, and snaps to 100% when the operation completes.
 */
export const TimedProgress: React.FC<TimedProgressProps> = ({
  active,
  estimatedDurationMs,
  steps,
  title,
  variant = 'inline',
  className,
}) => {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!active) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      startRef.current = null;
      setElapsed(0);
      return;
    }

    startRef.current = Date.now();
    setElapsed(0);
    intervalRef.current = setInterval(() => {
      if (startRef.current != null) {
        setElapsed(Date.now() - startRef.current);
      }
    }, TICK_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [active]);

  const fraction = estimatedDurationMs > 0 ? elapsed / estimatedDurationMs : 1;
  const pct = active
    ? Math.min(MAX_PCT, MAX_PCT * (1 - Math.exp(-EASE_K * fraction)))
    : 100;

  // Pick the active stage from the weighted time windows; once elapsed runs past
  // the last window, hold on the final stage rather than falling off the end.
  let currentLabel = '';
  if (steps.length > 0) {
    const totalWeight = steps.reduce((s, st) => s + Math.max(0, st.weight), 0) || 1;
    const target = fraction * totalWeight;
    let acc = 0;
    let stepIndex = steps.length - 1;
    for (let i = 0; i < steps.length; i++) {
      acc += Math.max(0, steps[i].weight);
      if (target < acc) {
        stepIndex = i;
        break;
      }
    }
    currentLabel = active ? steps[stepIndex].label : 'Done';
  }

  const rounded = Math.round(pct);

  // Keep the percentage glued to the title (or the stage label when there is no
  // title) so it sits right by the heading and is easy to spot at a glance.
  const pctLabel = (
    <span className="text-sm font-medium text-slate-700 tabular-nums">{rounded}%</span>
  );

  const bar = (
    <div className={variant === 'inline' ? className : undefined}>
      {title && (
        <div className="flex items-baseline justify-between mb-1">
          <p className="text-slate-800 font-medium">{title}</p>
          {pctLabel}
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-slate-600">{currentLabel}</span>
        {!title && pctLabel}
      </div>
      <div
        className="w-full bg-slate-200 rounded-full h-3"
        role="progressbar"
        aria-valuenow={rounded}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={title || currentLabel || 'Progress'}
      >
        <div
          className="bg-blue-600 h-3 rounded-full transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );

  if (variant === 'inline') {
    return bar;
  }

  return (
    <div className={`w-full max-w-2xl mx-auto py-6 ${className ?? ''}`}>{bar}</div>
  );
};

export default TimedProgress;
